import { apiSlice } from '../api/apiSlice'
import { AcronymsResponse, Project, ProjectsResponse, RequestProps, User } from '../../types'
import ProjectStatus from '../../types/enums/ProjectStatus'
import { changeEmptyStringsToNull, changeNullsToEmptyString } from '../../utils/falsyValuesConverter'
import Leader from '../../types/models/Leader'
import { UUID } from 'crypto'
import { BaseResponse } from '../../types/response/BaseResponse'

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getProjects: builder.query<ProjectsResponse, RequestProps>({
      query: (props: RequestProps) => ({
        url: 'projects',
        params: props,
      }),
      providesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    getProjectById: builder.query<ProjectDetails, Args>({
      query: ({ id, date }) => {
        if (date === undefined) date = ''
        return `/projects/${id}?date=${date}`
      },
      transformResponse: (responseData: ProjectDetails) => {
        const total = Object.values(responseData.hours ?? {}).reduce((a, b) => a + b, 0)
        return { ...responseData, project: changeNullsToEmptyString(responseData.project), totalHours: total }
      },
      providesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    createProject: builder.mutation({
      query: (project: Partial<ProjectModify>) => ({
        url: '/projects',
        method: 'POST',
        body: changeEmptyStringsToNull(project),
      }),
      invalidatesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    editProject: builder.mutation<Project, EditArgs>({
      query: ({ id, project }) => ({
        url: `/projects/${id}`,
        method: 'PUT',
        body: changeEmptyStringsToNull(project),
      }),
      invalidatesTags: (_result, _error, { project }) => {
        const leaderTags: { type: 'User'; id: string }[] =
          project.leaders?.map(leader => ({ type: 'User', id: `LEADER-${leader.leaderId}` })) || []

        return [{ type: 'Project', id: 'LIST' }, ...leaderTags]
      },
    }),

    deleteProject: builder.mutation<Project, string>({
      query: (id: string) => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    getAcronyms: builder.query<AcronymsResponse, RequestProps>({
      query: (props: RequestProps) => ({
        url: 'projects/acronyms',
        params: props,
      }),
      transformResponse: (responseData: AcronymsResponse) => {
        return responseData
      },
      providesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    getAcronymsImages: builder.query<BaseResponse<ProjectAcronymImages>, RequestProps>({
      query: (props: RequestProps) => ({
        url: 'projects/acronyms-images',
        params: props,
      }),
      transformResponse: (responseData: BaseResponse<ProjectAcronymImages>) => {
        return responseData
      },
      providesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    getLastProjects: builder.query<string[], { date: string }>({
      query: ({ date }) => `/projects/last-projects?date=${date}`,
      transformResponse: (responseData: string[]) => {
        return responseData
      },
      providesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    getParentProjects: builder.query<AcronymsResponse, RequestProps>({
      query: (props: RequestProps) => ({
        url: 'projects/parents',
        params: props,
      }),
      transformResponse: (responseData: AcronymsResponse) => {
        return responseData
      },
      providesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    getIsProjectFavoriteForUser: builder.query<boolean, { projectId: string; employeeId: string }>({
      query: ({ projectId }) => `/projects/favorite?projectId=${projectId}`,
      transformResponse: (responseData: boolean) => {
        return responseData
      },
      providesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    setIsProjectFavoriteForUser: builder.mutation({
      query: ({ projectId, isFavorite }) => ({
        url: `/projects/favorite?projectId=${projectId}&isFavorite=${isFavorite}`,
        method: 'POST',
      }),
      invalidatesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    swapLeadersForProjects: builder.mutation({
      query: (swapLeaders: SwapLeadersDTO) => ({
        url: '/projects/swap-leaders',
        method: 'PUT',
        body: swapLeaders,
      }),
    }),

    getProjectAcronymsByIds: builder.query<ProjectAcronym[], string[]>({
      query: ids => ({ url: 'projects/acronyms/byIds', params: { ids: ids.join(',') } }),
    }),

    getLeaderAndCoLeaders: builder.query<User[], { id: string }>({
      query: ({ id }) => ({
        url: 'projects/co-leaders',
        params: { id },
      }),
      providesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    getProjectSettingsById: builder.query<ProjectSettings, string>({
      query: id => {
        return `/projects/${id}/settings`
      },
      transformResponse: (responseData: ProjectSettings) => {
        return { ...responseData, project: changeNullsToEmptyString(responseData) }
      },
      providesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),

    changeFavoriteProjects: builder.mutation<void, UUID[]>({
      query: (projectIds: UUID[]) => ({
        url: 'projects/favorites',
        method: 'POST',
        body: projectIds,
      }),
      invalidatesTags: () => [{ type: 'Project', id: 'LIST' }],
    }),
  }),
})

type Args = {
  id: string
  date?: string
}

type WorkHoursDetails = {
  hours: number
  description: string
  date: string
}

export type EmployeeDetails = {
  employee: User
  schedule: WorkHoursDetails[]
  sumHours: number
}

export type ProjectDetails = {
  project: Project
  startDate: string
  endDate: string
  details: EmployeeDetails[]
  hours: Record<string, number>
  totalHours: number
  totalEmployees: number
  isParent: boolean
}

export type ProjectSettings = {
  leaders: Leader[]
  projectStatus: ProjectStatus
  operational: boolean
  loggable: boolean
  privacy: boolean
}

type ProjectModify = {
  name: string
  description: string
  acronym: string
  privacy: boolean
  projectStatus: string
  leaders: Array<{ projectId: string; leaderId: string; main: boolean }>
  driveUrl: string
  parentId: string
  operational: boolean
  version: number
  avatar: string
  extension: string
}

export type ProjectAcronym = {
  id: string
  acronym: string
  name: string
  projectStatus: ProjectStatus
  favorite: boolean | null
}

export type ProjectAcronymImages = {
  id: string
  acronym: string
  name: string
  avatar: string
  extension: string
  projectStatus: ProjectStatus
  favorite: boolean | null
}

type EditArgs = {
  id: string
  project: Partial<ProjectModify>
}

export type SwapLeadersDTO = {
  projectsIds: string[]
  exLeaderId: string
  newLeaderId: string
}

// Generated hooks
export const {
  useGetProjectsQuery,
  useLazyGetProjectsQuery,
  useCreateProjectMutation,
  useEditProjectMutation,
  useDeleteProjectMutation,
  useLazyGetProjectByIdQuery,
  useGetAcronymsQuery,
  useGetAcronymsImagesQuery,
  useGetParentProjectsQuery,
  useGetLastProjectsQuery,
  useLazyGetIsProjectFavoriteForUserQuery,
  useSetIsProjectFavoriteForUserMutation,
  useSwapLeadersForProjectsMutation,
  useGetProjectAcronymsByIdsQuery,
  useGetLeaderAndCoLeadersQuery,
  useLazyGetProjectSettingsByIdQuery,
  useChangeFavoriteProjectsMutation,
} = extendedApiSlice
